// frontend/src/components/staff/CancelledShiftDetailModal.js
//
// Workforce — SCHED-CANCELLED-VISUAL-ADMIN read-only Cancelled-detail modal.
//
// Visual spec: docs/design/SCHED-CANCELLED-VISUAL-ADMIN-design.md §3.
//
// Renders when admin clicks a cancelled shift block on the admin Schedule
// Builder grid. Deliberately separate from EditShiftModal because:
//   - Cancelled shifts cannot be edited (BE has no un-cancel endpoint).
//   - Cancelled shifts have additional forensic fields (cancelReason,
//     cancelledBy, cancelledAt) not exposed by EditShiftModal.
//   - "Cancelled shift" header sets the correct cognitive frame
//     ("this is history") vs. "Edit shift" ("I can change this").
//
// All fields are read-only; the single footer action is "Close".
//
// Defensive on missing / null cancellation metadata:
//   - empty cancelReason             -> render "—"
//   - missing cancelledBy            -> render "by [unknown]" italic
//   - missing notes on original shift -> render "—"
//
// IMPORTANT contract note (Marcus tech doc + production-safety):
//   The BE Shift serializer at /backend/controllers/workforceShiftsController.js
//   currently emits only `cancelledAt` (and `status`). It does NOT emit
//   `cancelReason` or `cancelledBy` on the shift wire shape today — those
//   fields live in the AuditLog metadata only. This modal renders defensively
//   so it does NOT crash when those fields are absent. Surfacing the reason
//   on the schedule block + modal requires a follow-up BE story to either
//   add `cancelReason` + `cancelledBy` to the Shift schema or join the
//   AuditLog on read. Flagged in Ready-for-Review.

import React from "react";
import moment from "moment-timezone";
import { FaInfoCircle } from "react-icons/fa";
import "./CancelledShiftDetailModal.css";

const STUDIO_TZ = "America/Phoenix";
const STUDIO_TZ_LABEL = "MST";

// Format helpers — kept inline (small) rather than imported from
// scheduleBuilderUtils to keep this modal self-contained and easy to
// surface from non-admin paths in a future story.

const formatShiftDateHeader = (startTime) => {
  const m = moment.tz(startTime, STUDIO_TZ);
  if (!m.isValid()) return "";
  return m.format("ddd MMM D, YYYY");
};

const formatTimeRange = (startTime, endTime) => {
  const s = moment.tz(startTime, STUDIO_TZ);
  const e = moment.tz(endTime, STUDIO_TZ);
  if (!s.isValid() || !e.isValid()) return "";
  const dayLabel = s.format("ddd MMM D");
  return `${s.format("h:mm A")} - ${e.format("h:mm A")} ${STUDIO_TZ_LABEL} (${dayLabel})`;
};

const formatCancelledTimestamp = (cancelledAt) => {
  const m = moment.tz(cancelledAt, STUDIO_TZ);
  if (!m.isValid()) return "";
  return `${m.format("MMM D, YYYY")} at ${m.format("h:mm A")} ${STUDIO_TZ_LABEL}`;
};

// Resolve a display name from either a populated user object or a raw
// userId string. cancelledBy in the BE is stored as ObjectId; if the
// controller starts to populate it we want to render firstName + lastName.
const resolveCancelledByDisplay = (cancelledBy) => {
  if (!cancelledBy) return null;
  if (typeof cancelledBy === "string") {
    return null; // raw ObjectId — caller should populate; render unknown
  }
  if (typeof cancelledBy === "object") {
    const first = cancelledBy.firstName || "";
    const last = cancelledBy.lastName || "";
    const composed = `${first} ${last}`.trim();
    if (composed) return composed;
    if (cancelledBy.email) return cancelledBy.email;
  }
  return null;
};

const CancelledShiftDetailModal = ({
  open,
  shift,
  staffDisplayName,
  staffRoleLabel,
  onClose,
}) => {
  // Esc closes (a11y).
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && typeof onClose === "function") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !shift) return null;

  const dateHeader = formatShiftDateHeader(shift.startTime);
  const timeRange = formatTimeRange(shift.startTime, shift.endTime);
  const cancelledStamp = shift.cancelledAt
    ? formatCancelledTimestamp(shift.cancelledAt)
    : "";

  const cancelReasonRaw =
    typeof shift.cancelReason === "string" ? shift.cancelReason.trim() : "";
  const reasonDisplay = cancelReasonRaw.length > 0 ? cancelReasonRaw : "—";

  const cancelledByDisplay = resolveCancelledByDisplay(shift.cancelledBy);

  const notesRaw = typeof shift.notes === "string" ? shift.notes.trim() : "";
  const notesDisplay = notesRaw.length > 0 ? notesRaw : "—";

  const composedStaff =
    staffDisplayName && staffRoleLabel
      ? `${staffDisplayName} — ${staffRoleLabel}`
      : staffDisplayName || staffRoleLabel || "—";

  return (
    <>
      <div
        className="cancelled-shift-detail-modal__backdrop"
        onClick={onClose}
        aria-hidden="true"
        data-testid="cancelled-shift-detail-modal-backdrop"
      />
      <div
        className="cancelled-shift-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancelled-shift-detail-modal-heading"
        data-testid="cancelled-shift-detail-modal"
      >
        <header className="cancelled-shift-detail-modal__header">
          <div>
            <h2
              id="cancelled-shift-detail-modal-heading"
              className="cancelled-shift-detail-modal__heading"
            >
              Cancelled shift
            </h2>
            {dateHeader && (
              <p
                className="cancelled-shift-detail-modal__subheading"
                data-testid="cancelled-shift-detail-modal-subheading"
              >
                For {dateHeader}
              </p>
            )}
          </div>
          <button
            type="button"
            className="cancelled-shift-detail-modal__close"
            onClick={onClose}
            aria-label="Close cancelled-shift detail"
            data-testid="cancelled-shift-detail-modal-close"
          >
            &times;
          </button>
        </header>

        <div className="cancelled-shift-detail-modal__body">
          <section className="cancelled-shift-detail-modal__section">
            <div className="cancelled-shift-detail-modal__label">Staff</div>
            <div
              className="cancelled-shift-detail-modal__value"
              data-testid="cancelled-shift-detail-modal-staff"
            >
              {composedStaff}
            </div>
          </section>

          <section className="cancelled-shift-detail-modal__section">
            <div className="cancelled-shift-detail-modal__label">Original time</div>
            <div
              className="cancelled-shift-detail-modal__value"
              data-testid="cancelled-shift-detail-modal-time"
            >
              {timeRange || "—"}
            </div>
          </section>

          <section className="cancelled-shift-detail-modal__section">
            <div className="cancelled-shift-detail-modal__label">Cancelled</div>
            <div
              className="cancelled-shift-detail-modal__value"
              data-testid="cancelled-shift-detail-modal-cancelled-at"
            >
              {cancelledStamp || "—"}
            </div>
            <div
              className={
                cancelledByDisplay
                  ? "cancelled-shift-detail-modal__by"
                  : "cancelled-shift-detail-modal__by cancelled-shift-detail-modal__by--unknown"
              }
              data-testid="cancelled-shift-detail-modal-cancelled-by"
            >
              by {cancelledByDisplay || "[unknown]"}
            </div>
          </section>

          <section className="cancelled-shift-detail-modal__section">
            <div className="cancelled-shift-detail-modal__label">Reason</div>
            <div
              className="cancelled-shift-detail-modal__value cancelled-shift-detail-modal__value--multiline"
              data-testid="cancelled-shift-detail-modal-reason"
            >
              {reasonDisplay}
            </div>
          </section>

          <section className="cancelled-shift-detail-modal__section">
            <div className="cancelled-shift-detail-modal__label">
              Notes (from original shift)
            </div>
            <div
              className="cancelled-shift-detail-modal__value cancelled-shift-detail-modal__value--muted cancelled-shift-detail-modal__value--multiline"
              data-testid="cancelled-shift-detail-modal-notes"
            >
              {notesDisplay}
            </div>
          </section>

          <div
            className="cancelled-shift-detail-modal__info-banner"
            role="note"
            data-testid="cancelled-shift-detail-modal-info-banner"
          >
            <span
              className="cancelled-shift-detail-modal__info-banner-icon"
              aria-hidden="true"
            >
              <FaInfoCircle />
            </span>
            <span>
              This shift is cancelled and cannot be re-opened. To re-schedule, create a new
              shift.
            </span>
          </div>
        </div>

        <footer className="cancelled-shift-detail-modal__footer">
          <button
            type="button"
            className="cancelled-shift-detail-modal__close-btn"
            onClick={onClose}
            data-testid="cancelled-shift-detail-modal-close-footer"
          >
            Close
          </button>
        </footer>
      </div>
    </>
  );
};

export default CancelledShiftDetailModal;
